'use client';

import { useEffect, useMemo, useState } from 'react';
import { getNetworkNameById } from '@/utils/networkNames';
import type { VerificationTemplateProps } from './types';

function resolveLogoSrc(raw: string | null | undefined) {
    const value = (raw ?? '').trim();
    if (!value) return null;
    if (/^(https?:\/\/|data:)/.test(value)) return value;
    if (value.startsWith('/')) {
        const apiBase = (process.env.NEXT_PUBLIC_API_BASE || '').replace(/\/+$/, '');
        return apiBase ? `${apiBase}${value}` : value;
    }
    return value;
}

function isHexColor(value: string) {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

function withFallback(value: string | null | undefined, fallback: string) {
    const trimmed = (value ?? '').trim();
    return trimmed && isHexColor(trimmed) ? trimmed : fallback;
}

const STATUS_COPY: Record<string, { icon: string; label: string; color: string; sentence: string }> = {
    valid: { icon: '✓', label: 'Verified', color: '#1F9D8C', sentence: 'has successfully completed' },
    revoked: { icon: '✕', label: 'Revoked', color: '#DC4C3E', sentence: 'was issued a certificate for' },
    expired: { icon: '!', label: 'Expired', color: '#D98A2B', sentence: 'completed, but this certificate has expired for' },
};

const CARD_INK = '#111827';

export default function TemplateDiplomaWide({
    certificate,
    tenant,
    verificationCode,
}: VerificationTemplateProps) {
    const issuerDisplayName = tenant?.issuerName || tenant?.name || 'Sigalmark';
    const logoSrc = resolveLogoSrc(tenant?.logoUrl);

    // Per-tenant logo scale, from Tenant.verificationTemplateConfig.logoHeight.
    // Classes are written out in full so Tailwind's JIT can see them.
    // Defaults to 'md', which is the historical size - existing tenants are unchanged.
    const LOGO_HEIGHT_CLASSES: Record<string, string> = {
        sm: 'h-8 sm:h-10',
        md: 'h-10 sm:h-12',
        lg: 'h-14 sm:h-20',
        xl: 'h-16 sm:h-24',
    };
    const logoHeightClass =
        LOGO_HEIGHT_CLASSES[String(tenant?.verificationTemplateConfig?.logoHeight ?? 'md')] ??
        LOGO_HEIGHT_CLASSES.md;

    const accent = withFallback(tenant?.tintElements, '#32D6D3');
    const pageWash = withFallback(tenant?.tintBackground, '#F8FAFC');

    const [issuedDisplay, setIssuedDisplay] = useState('');
    const [expiryDisplay, setExpiryDisplay] = useState('');

    useEffect(() => {
        const fmt = (d: string) =>
            new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
        setIssuedDisplay(fmt(certificate.issued));
        setExpiryDisplay(fmt(certificate.expiry));
    }, [certificate.issued, certificate.expiry]);

    const status = STATUS_COPY[certificate.status] ?? STATUS_COPY.valid;
    const networkName = useMemo(() => getNetworkNameById(certificate.networkId), [certificate.networkId]);

    return (
        <div
            className="min-h-screen w-full py-8 px-4 sm:px-6 lg:px-10"
            style={{ background: `radial-gradient(1400px 560px at 50% -8%, ${accent}26, transparent 70%), ${pageWash}` }}
        >
            <div
                className="relative w-full rounded-2xl border bg-white px-6 py-10 sm:px-10 sm:py-12 lg:px-16 lg:py-16 shadow-xl"
                style={{ borderColor: `${accent}55`, color: CARD_INK }}
            >
                {[
                    'top-2 left-2 border-t-2 border-l-2 rounded-tl-lg',
                    'top-2 right-2 border-t-2 border-r-2 rounded-tr-lg',
                    'bottom-2 left-2 border-b-2 border-l-2 rounded-bl-lg',
                    'bottom-2 right-2 border-b-2 border-r-2 rounded-br-lg',
                ].map((cls) => (
                    <span key={cls} className={`pointer-events-none absolute ${cls} w-5 h-5 sm:w-7 sm:h-7`} style={{ borderColor: accent }} />
                ))}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    {/* Left column — certificate information */}
                    <div className="text-left">
                        <div className="flex items-center justify-start mb-7">
                            {logoSrc ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img src={logoSrc} alt={`${issuerDisplayName} logo`} className={`${logoHeightClass} w-auto`} />
                            ) : (
                                <span className="text-xl font-semibold" style={{ color: CARD_INK }}>{issuerDisplayName}</span>
                            )}
                        </div>

                        <div className="mb-5">
                            <span className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1 text-xs sm:text-sm font-semibold uppercase tracking-wide"
                                style={{ color: status.color, backgroundColor: `${status.color}1A` }}>
                                <span>{status.icon}</span>{status.label}
                            </span>
                        </div>

                        <div className="flex items-center gap-3 mb-4">
                            <span className="h-px w-10 sm:w-14" style={{ backgroundColor: `${accent}88` }} />
                            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: accent }}>Certificate Verification</p>
                        </div>

                        <p className="text-4xl sm:text-5xl xl:text-6xl italic font-serif mb-4 break-words leading-tight" style={{ color: CARD_INK }}>{certificate.name}</p>

                        <p className="text-base mb-4" style={{ color: `${CARD_INK}CC` }}>{status.sentence}</p>
                        <div className="mb-8">
                            <span className="inline-block rounded-lg px-5 py-2.5 text-lg sm:text-xl font-bold"
                                style={{ color: CARD_INK, backgroundColor: `${accent}22`, border: `1px solid ${accent}55` }}>{certificate.competency}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 max-w-lg text-sm">
                            <div className="rounded-lg border p-4" style={{ borderColor: `${accent}33` }}>
                                <p className="text-xs uppercase tracking-wide" style={{ color: `${CARD_INK}99` }}>Issued</p>
                                <p className="font-medium" style={{ color: CARD_INK }}>{issuedDisplay || '—'}</p>
                            </div>
                            <div className="rounded-lg border p-4" style={{ borderColor: `${accent}33` }}>
                                <p className="text-xs uppercase tracking-wide" style={{ color: `${CARD_INK}99` }}>Valid until</p>
                                <p className="font-medium" style={{ color: CARD_INK }}>{expiryDisplay || '—'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Right column — certificate image */}
                    <div className="flex items-center justify-center">
                        {certificate.imageURI ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                                src={certificate.imageURI}
                                alt={`${certificate.name} certificate`}
                                className="w-full h-auto rounded-xl border shadow-md"
                                style={{ borderColor: `${accent}33` }}
                            />
                        ) : (
                            <div className="w-full aspect-[4/3] rounded-xl border flex items-center justify-center text-sm"
                                style={{ borderColor: `${accent}33`, color: `${CARD_INK}66` }}>
                                Certificate image unavailable
                            </div>
                        )}
                    </div>
                </div>

                {/* Bottom — verification number + explorer link, full width */}
                <div className="mt-12 pt-8 border-t" style={{ borderColor: `${accent}22` }}>
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                        <div className="rounded-xl p-5 sm:p-6 text-left flex-1" style={{ backgroundColor: `${accent}0D` }}>
                            <p className="text-xs uppercase tracking-wide" style={{ color: `${CARD_INK}99` }}>Verification number</p>
                            <p className="text-sm font-mono break-all" style={{ color: CARD_INK }}>{verificationCode}</p>
                            <p className="text-xs break-all mt-1" style={{ color: `${CARD_INK}99` }}>Contract: <span className="font-mono">{certificate.contract}</span> · Token #{certificate.tokenId} · {networkName}</p>
                        </div>
                        {certificate.explorerUrl ? (
                            <a href={certificate.explorerUrl} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90 whitespace-nowrap"
                                style={{ backgroundColor: CARD_INK, color: '#ffffff' }}>
                                View on blockchain explorer
                            </a>
                        ) : null}
                    </div>
                </div>
            </div>

            <p className="text-center text-xs mt-5" style={{ color: `${CARD_INK}99` }}>
                Issued by {issuerDisplayName} · Powered by{' '}
                <a href="https://sigalmark.com" target="_blank" rel="noopener noreferrer" className="underline">sigalmark™</a>
            </p>
        </div>
    );
}
